import React from 'react';
import { X, TrendingUp, TrendingDown, DollarSign, Calendar, Award } from 'lucide-react';
import { Participant } from '../../interface/HallOfFame';
import { useTheme } from '../ThemeContext';

interface ReceiptsDashboardProps {
  participant: Participant;
  communityAverage: { roi: number; history: Array<{ timestamp: string; value: number }> };
  onClose: () => void;
}

export const ReceiptsDashboard: React.FC<ReceiptsDashboardProps> = ({
  participant,
  communityAverage,
  onClose,
}) => {
  const { theme } = useTheme();

  const formatPercent = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getTierDefinition = (tier: string) => {
    const definitions: { [key: string]: string } = {
      S: '+50% or higher (Exceptional performance)',
      A: '+30% to +49.99% (Excellent performance)',
      B: '+20% to +29.99% (Good performance)',
      C: '+0% to +19.99% (Average performance)',
      D: '-20% to -0.01% (Below average performance)',
      F: 'Below -20% (Poor performance)',
    };
    return definitions[tier] || definitions.F;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen py-8 px-4 flex items-center justify-center">
        <div className="bg-theme-secondary rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-theme-secondary border-b border-theme-tertiary p-6 flex items-center justify-between z-10">
            <div>
              <h2 className="text-2xl font-bold text-theme-primary">{participant.name}</h2>
              <p className="text-theme-muted">{participant.socialHandle}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-theme-tertiary rounded-lg transition-colors"
            >
              <X size={24} className="text-theme-muted" />
            </button>
          </div>

          {/* Summary Stats */}
          <div className="p-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-theme-tertiary/30 p-4 rounded-lg">
              <div className="flex items-center gap-2 text-theme-muted mb-2">
                <DollarSign size={18} />
                <span className="text-sm">Current Value</span>
              </div>
              <div className="text-2xl font-bold text-theme-primary">
                {formatCurrency(participant.currentPortfolioValue ?? 0)}
              </div>
              <div className="text-xs text-theme-muted mt-1">
                Initial: {formatCurrency(participant.portfolioValue ?? 10000)}
              </div>
            </div>

            <div className="bg-theme-tertiary/30 p-4 rounded-lg">
              <div className="flex items-center gap-2 text-theme-muted mb-2">
                <Award size={18} />
                <span className="text-sm">Total ROI</span>
              </div>
              <div
                className={`text-2xl font-bold ${
                  (participant.totalROI ?? 0) >= 0 ? 'text-green-500' : 'text-red-500'
                }`}
              >
                {formatPercent(participant.totalROI ?? 0)}
              </div>
            </div>

            <div className="bg-theme-tertiary/30 p-4 rounded-lg">
              <div className="flex items-center gap-2 text-theme-muted mb-2">
                <TrendingUp size={18} />
                <span className="text-sm">vs Community</span>
              </div>
              <div
                className={`text-2xl font-bold ${
                  (participant.vsAverage ?? 0) > 0 ? 'text-green-500' : 'text-red-500'
                }`}
              >
                {formatPercent(participant.vsAverage ?? 0)}
              </div>
              <div className="text-xs text-theme-muted mt-1">
                Avg: {formatPercent(communityAverage.roi)}
              </div>
            </div>

            <div className="bg-theme-tertiary/30 p-4 rounded-lg">
              <div className="flex items-center gap-2 text-theme-muted mb-2">
                <Calendar size={18} />
                <span className="text-sm">Lock-In Date</span>
              </div>
              <div className="text-lg font-semibold text-theme-primary">
                {participant.lockInDate ? new Date(participant.lockInDate).toLocaleDateString() : 'N/A'}
              </div>
              <div className="text-xs text-theme-muted mt-1">
                {participant.lockInDate ? Math.floor(
                  (Date.now() - new Date(participant.lockInDate).getTime()) / (1000 * 60 * 60 * 24)
                ) : 0}{' '}
                days ago
              </div>
            </div>
          </div>


          {/* Stock Grid */}
          <div className="p-6">
            <h3 className="text-xl font-bold text-theme-primary mb-4">Individual Stock Performance</h3>
            {!Array.isArray(participant.stockPicks) || participant.stockPicks.length === 0 ? (
              <div className="text-center py-8 text-theme-muted">
                <p>No stock picks available</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                {participant.stockPicks.map((stock) => {
                const isWinner = stock.roi >= 0;
                return (
                  <div
                    key={stock.symbol}
                    className={`p-4 rounded-lg border-2 ${
                      isWinner
                        ? 'bg-green-500/10 border-green-500'
                        : 'bg-red-500/10 border-red-500'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="text-lg font-bold text-theme-primary">{stock.symbol}</h4>
                        <p className="text-xs text-theme-muted truncate">{stock.name || stock.symbol}</p>
                      </div>
                      {isWinner ? (
                        <TrendingUp className="text-green-500 flex-shrink-0" size={24} />
                      ) : (
                        <TrendingDown className="text-red-500 flex-shrink-0" size={24} />
                      )}
                    </div>

                    <div className="space-y-2">
                      <div>
                        <div className="text-xs text-theme-muted">Initial Price</div>
                        <div className="font-semibold text-theme-primary">
                          {formatCurrency(stock.initialPrice)}
                        </div>
                      </div>

                      <div>
                        <div className="text-xs text-theme-muted">Current Price</div>
                        <div className="font-semibold text-theme-primary">
                          {formatCurrency(stock.currentPrice)}
                        </div>
                      </div>

                      <div>
                        <div className="text-xs text-theme-muted">ROI</div>
                        <div
                          className={`text-xl font-bold ${
                            isWinner ? 'text-green-500' : 'text-red-500'
                          }`}
                        >
                          {formatPercent(stock.roi)}
                        </div>
                      </div>

                      <div>
                        <div className="text-xs text-theme-muted">Investment Value</div>
                        <div className="font-semibold text-theme-primary">
                          {formatCurrency(stock.investmentValue || 0)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              </div>
            )}
          </div>

          {/* Tier Badge */}
          <div className="p-6 border-t border-theme-tertiary">
            <div className="text-center">
              <div
                className="inline-block bg-theme-tertiary px-6 py-3 rounded-full cursor-help"
                title={getTierDefinition(participant.tier || 'F')}
              >
                <span className="text-2xl font-bold text-theme-primary">
                  Tier: {participant.tier}
                </span>
              </div>
              <div className="text-xs text-theme-muted mt-2">
                Hover for tier definition
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

